/**
 * Canonical CDN hosts for the TFI #3340 buckets: a TypeScript port of the
 * reference media_hosts.py (iv-soa docs/gen2-architecture/media-hosts).
 *
 * Vendor media-hosts.json and media-hosts.fixture.json byte-for-byte. Only the
 * 5 buckets in media-hosts.json are handled; anything else returns null, so
 * callers write `canonicalUrl(u) || u`.
 */
import * as mediaHostsJson from './media-hosts.json';

/** Phase 2 switch: emit the *.invideo.io aliases instead of the CloudFront ids. */
export const MEDIA_HOSTS_EMIT_INVIDEO_IO = false;

export interface MediaHostsBucket {
  cdn_host: string;
  alias_host: string;
  cdn_hosts: string[];
  has_resized_variants: boolean;
}

type MediaHostsFile = { buckets: { [bucket: string]: MediaHostsBucket } };

// ts-jest (commonjs) gives the object itself, webpack puts it on default
const MEDIA_HOSTS_FILE = ((mediaHostsJson as any).default || mediaHostsJson) as MediaHostsFile;

export const MEDIA_HOSTS_BUCKETS: { [bucket: string]: MediaHostsBucket } = MEDIA_HOSTS_FILE.buckets;

export const CDN_HOST_TO_BUCKET: { [host: string]: string } = {};
Object.keys(MEDIA_HOSTS_BUCKETS).forEach((bucket) => {
  MEDIA_HOSTS_BUCKETS[bucket].cdn_hosts.forEach((host) => {
    CDN_HOST_TO_BUCKET[host] = bucket;
  });
});

const REGION = '[a-z]{2}(?:-[a-z]+)+-\\d';
// s3.amazonaws.com, s3.<region>, s3-<region>, s3.dualstack.<region>
const S3_REST = `s3(?:\\.amazonaws\\.com|[.-]${REGION}\\.amazonaws\\.com|\\.dualstack\\.${REGION}\\.amazonaws\\.com)`;
const PATH_STYLE_RE = new RegExp(`^${S3_REST}$`);
const VHOST_RE = new RegExp(`^([a-z0-9.-]+)\\.${S3_REST}$`);
const WEBSITE_RE = new RegExp(`^([a-z0-9.-]+)\\.s3-website[.-]${REGION}\\.amazonaws\\.com$`);

const RESIZED_RE = /^\d{2,4}x\d{2,4}\//;
const DROP_QUERY = ['awsaccesskeyid', 'signature', 'expires'];
const SIGNED_CDN_QUERY = ['key-pair-id', 'policy', 'signature'];
const KEY_SAFE = "/%!$&'()*+,;=:@-._~";
const QUERY_SAFE = "/:@!$'()*,;~";
// urllib.parse.urlsplit: scheme, netloc, path, query, fragment
const URL_RE = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/([^/?#]*)([^?#]*)(?:\?([^#]*))?(?:#(.*))?$/;

type Form = 'rest' | 'website' | 'cdn';

interface Parsed {
  bucket: string;
  form: Form;
  path: string;
  query: string;
  fragment: string;
}

/** urllib.parse.quote: UTF-8 percent-encoding, keeping letters, digits, '_.-~' and `safe`. */
function quote(value: string, safe: string): string {
  let out = '';
  for (const ch of value) {
    if (/[A-Za-z0-9_.~-]/.test(ch) || safe.includes(ch)) {
      out += ch;
    } else {
      out += encodeURIComponent(ch);
    }
  }
  return out;
}

/** urllib.parse.unquote: decodes valid %XX runs as UTF-8 and leaves the rest as it is. */
function unquote(value: string): string {
  return value.replace(/(?:%[0-9A-Fa-f]{2})+/g, (run) => {
    try {
      return decodeURIComponent(run);
    } catch (e) {
      return run;
    }
  });
}

function emitHost(bucket: string): string {
  const cfg = MEDIA_HOSTS_BUCKETS[bucket];
  return MEDIA_HOSTS_EMIT_INVIDEO_IO ? cfg.alias_host : cfg.cdn_host;
}

function parse(url: unknown): Parsed | null {
  if (typeof url !== 'string') {
    return null;
  }
  const match = URL_RE.exec(url.trim());
  if (!match) {
    return null;
  }
  const [, scheme, netloc, rawPath, query = '', fragment = ''] = match;
  const hostPort = netloc.slice(netloc.lastIndexOf('@') + 1);
  const colon = hostPort.lastIndexOf(':');
  let hostname = hostPort;
  if (colon !== -1) {
    hostname = hostPort.slice(0, colon);
    const port = hostPort.slice(colon + 1);
    if (port && (!/^\d+$/.test(port) || !['80', '443'].includes(String(Number(port))))) {
      return null;
    }
  }
  if (!['http', 'https'].includes(scheme.toLowerCase())) {
    return null;
  }
  const host = hostname.toLowerCase().replace(/\.+$/, '');
  if (!rawPath.startsWith('/')) {
    return null;
  }
  let path = rawPath.slice(1);
  let bucket: string;
  let form: Form;

  if (host in CDN_HOST_TO_BUCKET) {
    bucket = CDN_HOST_TO_BUCKET[host];
    form = 'cdn';
  } else if (PATH_STYLE_RE.test(host)) {
    const slash = path.indexOf('/');
    bucket = slash === -1 ? path : path.slice(0, slash);
    path = slash === -1 ? '' : path.slice(slash + 1);
    form = 'rest';
  } else {
    let m = VHOST_RE.exec(host);
    form = 'rest';
    if (!m) {
      m = WEBSITE_RE.exec(host);
      form = 'website';
    }
    if (!m) {
      return null;
    }
    bucket = m[1];
  }

  if (!Object.prototype.hasOwnProperty.call(MEDIA_HOSTS_BUCKETS, bucket) || !path) {
    return null;
  }
  return { bucket, form, path, query, fragment };
}

function cleanQuery(query: string, form: Form): string | null {
  if (!query) {
    return '';
  }
  // parse_qsl(keep_blank_values=True)
  const pairs = query
    .split('&')
    .filter((part) => part)
    .map((part) => {
      const eq = part.indexOf('=');
      const name = eq === -1 ? part : part.slice(0, eq);
      const value = eq === -1 ? '' : part.slice(eq + 1);
      return [unquote(name.replace(/\+/g, ' ')), unquote(value.replace(/\+/g, ' '))];
    });
  const names = pairs.map(([name]) => name.toLowerCase());
  if (form === 'cdn') {
    // signed CloudFront URLs are not ours to rewrite
    return names.some((name) => SIGNED_CDN_QUERY.includes(name)) ? null : query;
  }
  const kept = pairs.filter(([name]) => {
    const lower = name.toLowerCase();
    return !(lower.startsWith('x-amz-') || DROP_QUERY.includes(lower));
  });
  if (kept.length === pairs.length) {
    return query;
  }
  return kept.map(([name, value]) => `${quote(name, QUERY_SAFE)}=${quote(value, QUERY_SAFE)}`).join('&');
}

function canonicalPath(path: string, form: Form): string {
  if (form === 'cdn') {
    return path;
  }
  return quote(path.replace(/\+/g, form === 'website' ? '%2B' : '%20'), KEY_SAFE);
}

function build(bucket: string, path: string, query: string, fragment: string): string {
  return `https://${emitHost(bucket)}/${path}${query ? `?${query}` : ''}${fragment ? `#${fragment}` : ''}`;
}

/** CDN URL for a URL of one of the 5 buckets in any spelling; null otherwise. */
export function canonicalUrl(url: string): string | null {
  const parsed = parse(url);
  if (!parsed) {
    return null;
  }
  const query = cleanQuery(parsed.query, parsed.form);
  if (query === null) {
    return null;
  }
  return build(parsed.bucket, canonicalPath(parsed.path, parsed.form), query, parsed.fragment);
}

/**
 * [bucket, decoded S3 object key] for a URL of one of the 5 buckets; null otherwise.
 * REST and CDN forms read '+' as a space (the post-lock CloudFront origin is REST);
 * website forms read it literally.
 */
export function bucketAndKey(url: string): [string, string] | null {
  const parsed = parse(url);
  if (!parsed) {
    return null;
  }
  if (parsed.form === 'cdn' && cleanQuery(parsed.query, parsed.form) === null) {
    return null;
  }
  const path = parsed.form !== 'website' ? parsed.path.replace(/\+/g, ' ') : parsed.path;
  return [parsed.bucket, unquote(path)];
}

/** canonicalUrl without a leading <N>x<N>/ resize prefix; null if there is none. */
export function unresizedUrl(url: string): string | null {
  const parsed = parse(url);
  if (!parsed) {
    return null;
  }
  if (!MEDIA_HOSTS_BUCKETS[parsed.bucket].has_resized_variants || !RESIZED_RE.test(parsed.path)) {
    return null;
  }
  const query = cleanQuery(parsed.query, parsed.form);
  if (query === null) {
    return null;
  }
  const path = parsed.path.replace(RESIZED_RE, '');
  if (!path) {
    return null;
  }
  return build(parsed.bucket, canonicalPath(path, parsed.form), query, parsed.fragment);
}
